import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useCustomerStore } from './customer'
import { deleteField, doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'
import { firestore } from '@/firebase'
import {
  availabilitySlotCustomerDraftsSchema,
  availabilitySlotsSchema,
  type AvailabilitySlotCustomerDrafts,
  type TellescopeAvailabilitySlots,
} from '@gh/common/models'

import createLogger from '@/utils/logger'
import { tellescopeVendorSyncApi } from '@/components/cloudFunctions/tellescopeVendorSync'
import { DateTime } from 'luxon'
import { useCurrentTime } from '@/composables/useCurrentTime'

const logger = createLogger('stores/tellescopeAvailability.ts')

const TIME_THRESHOLD_FOR_FRESH_DATA = 1000 * 60 * 10 // 10 minutes

export const useTellescopeAvailabilityStore = defineStore('tellescopeAvailability', () => {
  const customerStore = useCustomerStore()
  const customer = computed(() => customerStore.customer)

  let unsubscribeTellescopeAvailabilitySnapshot: (() => void) | null = null
  let unsubscribeTellescopeAvailabilityDraftSelectsSnapshot: (() => void) | null = null

  const tellescopeAvailability = ref<TellescopeAvailabilitySlots | null>(null)
  const tellescopeAvailabilityDraftSelects = ref<AvailabilitySlotCustomerDrafts | null>(null)
  const shouldEnsureFreshData = ref(false)

  const documentAvailabilitySlotsId = 'all_availability_slots'
  const customerDraftSelectsDocumentId = 'customer_draft_selects'
  const documentReferenceCustomerDraftSelects = doc(
    firestore,
    'tellescope_availability_slots',
    customerDraftSelectsDocumentId,
  )
  const documentReferenceAvailabilitySlots = doc(
    firestore,
    'tellescope_availability_slots',
    documentAvailabilitySlotsId,
  )

  watch(
    customer,
    async (newVal) => {
      logger.info('customer changed', { newVal, customerId: newVal?.id })
      if (newVal) {
        await retrieveTellescopeAvailabilityFromFirestore()
        logger.info('Subscriptions set up for customer', { customerId: newVal.id })
      } else {
        if (unsubscribeTellescopeAvailabilitySnapshot) {
          unsubscribeTellescopeAvailabilitySnapshot()
          unsubscribeTellescopeAvailabilitySnapshot = null
        }
        if (unsubscribeTellescopeAvailabilityDraftSelectsSnapshot) {
          unsubscribeTellescopeAvailabilityDraftSelectsSnapshot()
          unsubscribeTellescopeAvailabilityDraftSelectsSnapshot = null
        }
        tellescopeAvailability.value = null
        tellescopeAvailabilityDraftSelects.value = null
      }
    },
    { immediate: true },
  )

  async function retrieveTellescopeAvailabilityFromFirestore() {
    logger.info('retrieveTellescopeAvailabilityFromFirestore', { customer: customer.value })
    if (unsubscribeTellescopeAvailabilitySnapshot) {
      unsubscribeTellescopeAvailabilitySnapshot()
    }
    if (unsubscribeTellescopeAvailabilityDraftSelectsSnapshot) {
      unsubscribeTellescopeAvailabilityDraftSelectsSnapshot()
    }

    const [availabilityDoc, draftsDoc] = await Promise.all([
      getDoc(documentReferenceAvailabilitySlots),
      getDoc(documentReferenceCustomerDraftSelects),
    ])

    if (!availabilityDoc.exists() || !draftsDoc.exists()) {
      logger.warn('tellescopeAvailability document not found, forcing refresh')
      await forceRefresh()
    }

    unsubscribeTellescopeAvailabilitySnapshot = onSnapshot(
      documentReferenceAvailabilitySlots,
      (docSnap) => {
        if (docSnap.exists()) {
          const { success, data, error } = availabilitySlotsSchema.safeParse({
            id: docSnap.id,
            ...docSnap.data(),
          })
          if (!success) {
            logger.error('Error parsing tellescopeAvailability data', {
              data,
              error,
              snap: docSnap.data(),
            })
            return
          }
          tellescopeAvailability.value = data
          logger.info('tellescopeAvailability updated via onSnapshot', {
            customerId: customer.value?.id,
            availabilityBlocksCount: data.availabilityBlocks?.length || 0,
            updatedAt: data.updatedAt,
          })
        } else {
          logger.error('tellescopeAvailability document not found, forcing refresh')
        }
      },
      (error) => {
        logger.error('Error in onSnapshot for availability slots', { error })
      },
    )

    unsubscribeTellescopeAvailabilityDraftSelectsSnapshot = onSnapshot(
      documentReferenceCustomerDraftSelects,
      (docSnap) => {
        if (docSnap.exists()) {
          const { success, data, error } = availabilitySlotCustomerDraftsSchema.safeParse({
            id: docSnap.id,
            ...docSnap.data(),
          })
          if (!success) {
            logger.error('Error parsing tellescopeAvailabilityDraftSelects data', {
              data,
              error,
              snap: docSnap.data(),
            })
            return
          }
          tellescopeAvailabilityDraftSelects.value = data
          const customerDraftsCount = Object.keys(data.customerDrafts || {}).length
          logger.info('tellescopeAvailabilityDraftSelects updated via onSnapshot', {
            customerId: customer.value?.id,
            customerDraftsCount,
            hasMyDraft: customer.value ? !!data.customerDrafts?.[customer.value.id] : false,
          })
        } else {
          logger.error('tellescopeAvailabilityDraftSelects document not found, forcing refresh')
        }
      },
      (error) => {
        logger.error('Error in onSnapshot for customer draft selects', { error })
      },
    )
  }

  function signOut() {
    if (unsubscribeTellescopeAvailabilitySnapshot) {
      unsubscribeTellescopeAvailabilitySnapshot()
    }
    if (unsubscribeTellescopeAvailabilityDraftSelectsSnapshot) {
      unsubscribeTellescopeAvailabilityDraftSelectsSnapshot()
    }
    tellescopeAvailability.value = null
    tellescopeAvailabilityDraftSelects.value = null
    shouldEnsureFreshData.value = false
  }

  const availabilitySlots = computed(() => {
    return (
      tellescopeAvailability.value?.availabilityBlocks.map(
        (slot: TellescopeAvailabilitySlots['availabilityBlocks'][number]) => ({
          ...slot,
          startDate: DateTime.fromMillis(slot.startTimeInMS).startOf('day').toJSDate(),
          startDateString: DateTime.fromMillis(slot.startTimeInMS).toFormat('yyyy-MM-dd'),
        }),
      ) ?? []
    )
  })

  const updatedAt = computed(() => {
    return tellescopeAvailability.value?.updatedAt ?? null
  })

  function ensureFreshData(freshData: boolean = true) {
    shouldEnsureFreshData.value = freshData
  }

  const { currentTime: everyMinute } = useCurrentTime({ durationInMinutes: 1 })

  const { currentTime: everyFiveMinutes } = useCurrentTime({ durationInMinutes: 5 })

  const forceRefresh = async () => {
    await tellescopeVendorSyncApi.getAvailabilitySlots({})
  }
  const selectedSlotId = ref<string | null>(null)
  const makeSlotAsSelected = async (slotId: string) => {
    if (!customer.value) {
      logger.error('makeSlotAsSelected called but customer is null', { slotId })
      return
    }
    const customerId = customer.value.id
    logger.info('makeSlotAsSelected called', { customerId, slotId })
    try {
      await updateDoc(documentReferenceCustomerDraftSelects, {
        [`customerDrafts.${customerId}.selectedAt`]: new Date(),
        [`customerDrafts.${customerId}.slotId`]: slotId,
      })
      selectedSlotId.value = slotId
      logger.info('Slot marked as selected in Firestore', { customerId, slotId })
    } catch (error) {
      logger.error('Error marking slot as selected', { customerId, slotId, error })
      throw error
    }
  }

  const unmarkSlotAsSelected = async () => {
    if (!customer.value) {
      logger.error('unmarkSlotAsSelected called but customer is null')
      return
    }
    const customerId = customer.value.id
    logger.info('unmarkSlotAsSelected called', { customerId })
    try {
      await updateDoc(documentReferenceCustomerDraftSelects, {
        [`customerDrafts.${customerId}`]: deleteField(),
      })
      selectedSlotId.value = null
      logger.info('Slot unmarked in Firestore', { customerId })
    } catch (error) {
      logger.error('Error unmarking slot', { customerId, error })
      throw error
    }
  }

  watch([shouldEnsureFreshData, updatedAt, everyMinute], async (newVal) => {
    const [shouldEnsureFreshData, updatedAt] = newVal
    if (
      shouldEnsureFreshData &&
      updatedAt &&
      updatedAt.getTime() < Date.now() - TIME_THRESHOLD_FOR_FRESH_DATA
    ) {
      await forceRefresh()
    }
  })

  watch([everyFiveMinutes], async () => {
    if (selectedSlotId.value) {
      // to ensure it doesnt get deleted by the server
      await makeSlotAsSelected(selectedSlotId.value)
    }
  })

  return {
    availabilitySlots,
    tellescopeAvailabilityDraftSelects,
    updatedAt,
    signOut,
    ensureFreshData,
    forceRefresh,
    makeSlotAsSelected,
    unmarkSlotAsSelected,
  }
})
